import type {
  CollegeStrategicPillar,
  CollegeGoal,
  FocusArea,
  Tactic,
} from "@/types/entities";

/** A single node in the derived ladder. */
export interface LadderNode {
  id: string;
  name: string;
}

/**
 * The derived strategic context ladder for an Objective.
 *
 * Each field is null when the walk cannot reach that layer.
 * `focusArea` is always present when the ladder is non-null
 * (it is the entry point); higher layers may be absent if
 * the Focus Area has no College Goal links.
 */
export interface ObjectiveLadder {
  pillar: LadderNode | null;
  goal: LadderNode | null;
  focusArea: LadderNode;
  strategicInitiative: LadderNode | null;
}

interface LadderData {
  focusAreas: FocusArea[];
  tactics: Tactic[];
  collegeGoals: CollegeGoal[];
  pillars: CollegeStrategicPillar[];
}

/**
 * Derive the strategic context ladder for an Objective from cached data.
 *
 * Walk: Objective → Focus Area (first from focus_area_ids) →
 *       College Goal (first from FA.college_goal_ids by sort order) →
 *       College Strategic Pillar (via goal.college_strategic_pillar_id).
 *
 * Also resolves the Strategic Initiative (first from tactic_ids).
 *
 * Returns null when the Objective has no Focus Area selected.
 */
export function buildObjectiveLadder(
  focusAreaIds: string[] | undefined | null,
  tacticIds: string[] | undefined | null,
  data: LadderData,
): ObjectiveLadder | null {
  const primaryFaId = focusAreaIds?.[0];
  if (!primaryFaId) return null;

  const focusArea = data.focusAreas.find((fa) => fa.id === primaryFaId);
  if (!focusArea) return null;

  // Resolve Strategic Initiative
  const primaryTacticId = tacticIds?.[0];
  const tactic = primaryTacticId
    ? data.tactics.find((t) => t.id === primaryTacticId)
    : undefined;

  const strategicInitiative: LadderNode | null = tactic
    ? {
        id: tactic.id,
        name: tactic.reference_code
          ? `SI-${tactic.reference_code}: ${tactic.name}`
          : tactic.name,
      }
    : null;

  // Walk up: Focus Area → College Goal → Pillar
  const goalIds = focusArea.college_goal_ids ?? [];
  if (goalIds.length === 0) {
    return {
      pillar: null,
      goal: null,
      focusArea: { id: focusArea.id, name: focusArea.name },
      strategicInitiative,
    };
  }

  // Pick the first linked College Goal (by the order returned from the API,
  // which is sort_order). If the FA has multiple goals, use the first.
  const goal = findFirstById(data.collegeGoals, goalIds);
  if (!goal) {
    return {
      pillar: null,
      goal: null,
      focusArea: { id: focusArea.id, name: focusArea.name },
      strategicInitiative,
    };
  }

  const pillar = data.pillars.find(
    (p) => p.id === goal.college_strategic_pillar_id,
  );

  return {
    pillar: pillar ? { id: pillar.id, name: pillar.name } : null,
    goal: { id: goal.id, name: goal.name },
    focusArea: { id: focusArea.id, name: focusArea.name },
    strategicInitiative,
  };
}

/**
 * Find the first item from `items` whose ID appears in `ids`,
 * preserving the order of `ids`.
 */
function findFirstById<T extends { id: string }>(
  items: T[],
  ids: string[],
): T | undefined {
  for (const id of ids) {
    const item = items.find((i) => i.id === id);
    if (item) return item;
  }
  return undefined;
}
